import React, { memo, useCallback, useEffect, useLayoutEffect, useRef } from 'react';
import { Segment, SegmentedControl } from 'baseui/segmented-control';
import { SnackbarProvider } from 'baseui/snackbar';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import useShortcut from '../../image/labelling/providers/hooks/use-shortcut';
import { useReviewMode } from '../providers/review-mode-providers';
import useAnalyticsForReviewSidePanel from './hooks/useAnalyticsForReviewSidePanel';
import { panelTabAtom, pendingAnnotationsAtom, reviewAnnotationsAtom } from './atoms';
import { BatchPanel } from './batch-review';
import { ClassificationPanel } from './classification-panel';
import { CompletedPanel } from './completed-panel';
import { REVIEW_MODE_PANEL_TABS } from './constants';
import { useReviewFeature } from './hooks';
import { PendingPanel } from './pending-panel';
import { ReviewDrawer } from './review-drawer';
import { ReviewModal } from './review-modal';
import { Container, segmentedControlOverrides } from './styled-component-overrides';
import type { ReviewAnnotationsT } from './types';
type Props = {
  annotations: ReviewAnnotationsT;
  selectedAnnotationId?: string;
};
const ReviewModeSidePanelComponent = ({
  annotations,
  selectedAnnotationId
}: Props) => {
  const prevAnnotationsRef = useRef<ReviewAnnotationsT | undefined>(undefined);
  const {
    selectAnnotation
  } = useReviewMode();
  const [panelTab, setPanelTab] = useAtom(panelTabAtom);
  const setReviewAnnotations = useSetAtom(reviewAnnotationsAtom);
  const pendingAnnotations = useAtomValue(pendingAnnotationsAtom);
  const {
    acceptRejectLabel,
    enableBatchMode
  } = useReviewFeature();
  const {
    registerHotKeys
  } = useShortcut();
  const {
    sendEventReviewAnnotationsVisibility
  } = useAnalyticsForReviewSidePanel();
  const modalRootRef = useRef<HTMLDivElement>(null);
  const showPendingAndCompletedPanel = acceptRejectLabel && !enableBatchMode;
  const showClassificationPanel = !acceptRejectLabel && !enableBatchMode;
  const showBatchPanel = enableBatchMode;

  // Syncing the value in render block to ensure children render with correct data.
  // eslint-disable-next-line react-hooks/refs
  if (annotations !== prevAnnotationsRef.current) {
    setReviewAnnotations(annotations);
    // eslint-disable-next-line react-hooks/refs
    prevAnnotationsRef.current = annotations;
  }
  return <SnackbarProvider>
      <Container ref={modalRootRef}>
        {showPendingAndCompletedPanel && <>
            <SegmentedControl activeKey={panelTab} onChange={({
          activeKey
        }) => setPanelTab((activeKey as string))} overrides={segmentedControlOverrides}>
              <Segment key={REVIEW_MODE_PANEL_TABS.PENDING} label="Pending" badge={pendingAnnotations.length} />
              <Segment key={REVIEW_MODE_PANEL_TABS.COMPLETED} label="Completed" />
            </SegmentedControl>

            {panelTab === REVIEW_MODE_PANEL_TABS.PENDING && <PendingPanel selectedAnnotationId={selectedAnnotationId} />}
            {panelTab === REVIEW_MODE_PANEL_TABS.COMPLETED && <CompletedPanel selectAnnotation={selectAnnotation} />}
          </>}

        {showClassificationPanel && <ClassificationPanel annotationIds={pendingAnnotations} selectedAnnotationId={selectedAnnotationId} />}

        {showBatchPanel && <BatchPanel selectedId={selectedAnnotationId} />}

        <ReviewDrawer />
        <ReviewModal modalRootRef={modalRootRef} />
      </Container>
    </SnackbarProvider>;
};
export const ReviewModeSidePanel = memo(ReviewModeSidePanelComponent);